/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * WhatsApp Business setup under Settings → Integrations → Social.
 *
 * Masked-secret discipline matches TwilioSetupPage: stored tokens render as
 * bullets and only round-trip when the admin retypes them.
 */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Lock,
  OpenInNew,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import { useCapability } from "auth/usePermission";
import { usePermissionsContext } from "auth/PermissionsProvider";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { getApiErrorMessage } from "utils/apiErrors";

const META_DEVELOPERS_URL = "https://developers.facebook.com/";
const META_BUSINESS_URL = "https://business.facebook.com/";
const WA_GREEN = "#25D366";
const MASK = "••••••••••••••••";

function InstructionCard({ title, children, action }) {
  return (
    <MDBox
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#FAFBFC",
      }}
    >
      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        {title}
      </MDTypography>
      <MDTypography variant="body2" color="text" component="div" sx={{ lineHeight: 1.7 }}>
        {children}
      </MDTypography>
      {action && <MDBox mt={2}>{action}</MDBox>}
    </MDBox>
  );
}

function NumberedSteps({ steps }) {
  return (
    <MDBox
      component="ol"
      sx={{
        m: 0,
        pl: 2.5,
        "& li": { mb: 1.25, lineHeight: 1.65 },
        "& li::marker": { color: WA_GREEN, fontWeight: 700 },
      }}
    >
      {steps.map((step) => (
        <MDTypography key={step} component="li" variant="body2" color="text">
          {step}
        </MDTypography>
      ))}
    </MDBox>
  );
}

function CopyField({ label, value, helper }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* field is selectable regardless */
    }
  };
  return (
    <TextField
      fullWidth
      label={label}
      value={value || ""}
      helperText={helper}
      margin="normal"
      size="small"
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? "Copied" : "Copy"}>
              <span>
                <IconButton onClick={copy} edge="end" disabled={!value} size="small">
                  {copied ? <CheckCircle color="success" fontSize="small" /> : <ContentCopy fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}

/**
 * A secret that is already stored stays masked and read-only until the admin
 * presses Replace. Clearing the field on focus made a saved credential look
 * like it had been wiped by a stray click.
 */
function SecretField({
  label,
  value,
  onChange,
  stored,
  storedHint,
  onReplace,
  onKeep,
  visible,
  onToggleVisible,
  locked,
  helper,
}) {
  // Showing the tail of what is stored is the only way an admin can tell a
  // wrong-but-saved credential (Meta answers "Cannot parse access token")
  // from a missing one.
  const storedText = storedHint
    ? `Saved (ends ${String(storedHint).replace(/[•*]/g, "").trim() || "····"}). Press Replace to enter a new one.`
    : `${label} is saved. Press Replace to enter a new one.`;
  return (
    <TextField
      fullWidth
      label={label}
      type={visible && !stored ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      margin="normal"
      size="small"
      disabled={locked}
      autoComplete="off"
      inputProps={{ autoComplete: "off", spellCheck: "false", "data-lpignore": "true" }}
      helperText={stored ? storedText : helper}
      InputProps={{
        readOnly: stored,
        endAdornment: (
          <InputAdornment position="end">
            {locked ? (
              <Lock fontSize="small" color="action" />
            ) : stored ? (
              <MDButton variant="text" color="info" size="small" onClick={onReplace}>
                Replace
              </MDButton>
            ) : (
              <>
                <IconButton edge="end" size="small" onClick={onToggleVisible}>
                  {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
                {onKeep && (
                  <MDButton variant="text" color="secondary" size="small" onClick={onKeep}>
                    Cancel
                  </MDButton>
                )}
              </>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}

SecretField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  stored: PropTypes.bool,
  storedHint: PropTypes.string,
  onReplace: PropTypes.func.isRequired,
  onKeep: PropTypes.func,
  visible: PropTypes.bool,
  onToggleVisible: PropTypes.func.isRequired,
  locked: PropTypes.bool,
  helper: PropTypes.string,
};

SecretField.defaultProps = {
  value: "",
  stored: false,
  storedHint: "",
  onKeep: null,
  visible: false,
  locked: false,
  helper: "",
};

export default function WhatsAppSettingsPanel({ isPlayground = false }) {
  const { axiosInstance, user } = useAuth();
  const canManageIntegrations = useCapability("MANAGE_INTEGRATIONS");
  const { isSuper, mode: permissionMode, ready: permissionsReady } = usePermissionsContext();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const whatsappEnabled = !entitlementsLoading && isModuleEnabled("WHATSAPP");
  // Match backend require_capability: role may be an enum object ({ value }) or a string.
  const roleValue = String(user?.role?.value ?? user?.role ?? "").toLowerCase();
  const legacyAdmin = roleValue === "system_admin";
  const canManage = Boolean(
    whatsappEnabled &&
      (canManageIntegrations ||
        isSuper ||
        legacyAdmin ||
        (permissionMode !== "enforce" && legacyAdmin) ||
        (!permissionsReady && legacyAdmin))
  );

  const [loading, setLoading] = useState(true);
  // One action at a time, named. Subscribe and Send Test save the form first,
  // and a shared `saving` flag made the Save button spin for work the user
  // never asked it to do.
  const [busy, setBusy] = useState(null);
  const isBusy = busy !== null;
  // Inline banner (success | error) — Snackbar alone is easy to miss when
  // scrolled down to Subscribe / Test.
  const [feedback, setFeedback] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [settings, setSettings] = useState(null);
  const [businessAccountId, setBusinessAccountId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [testRecipient, setTestRecipient] = useState("");

  const [accessToken, setAccessToken] = useState("");
  const [accessTokenEdited, setAccessTokenEdited] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);

  const [appSecret, setAppSecret] = useState("");
  const [appSecretEdited, setAppSecretEdited] = useState(false);
  const [showAppSecret, setShowAppSecret] = useState(false);

  // Only playground is view-only. Entitlement already gates the panel above;
  // never grey out Save when the form is visible — that blocked first-time
  // credential setup while Test still returned "must be saved".
  const fieldsLocked = Boolean(isPlayground);

  // Feedback is scoped to the step that produced it so the banner appears next
  // to the button the user pressed, and only there.
  const showSuccess = (scope, message) => setFeedback({ severity: "success", scope, message });
  const showError = (scope, message) => setFeedback({ severity: "error", scope, message });

  const apply = useCallback((data) => {
    setSettings(data);
    setBusinessAccountId(data.business_account_id || "");
    setPhoneNumberId(data.phone_number_id || "");
    setVerifyToken(data.verify_token || "");
    // Masked placeholders only — never put a real secret in the DOM.
    setAccessToken(data.has_access_token ? MASK : "");
    setAccessTokenEdited(false);
    setShowAccessToken(false);
    setAppSecret(data.has_app_secret ? MASK : "");
    setAppSecretEdited(false);
    setShowAppSecret(false);
  }, []);

  const load = useCallback(async () => {
    if (isPlayground) {
      apply({
        id: 0,
        business_account_id: "",
        phone_number_id: "",
        verify_token: "example-verify-token",
        has_access_token: false,
        has_app_secret: false,
        webhook_subscribed: false,
        connected: false,
        encryption_available: true,
        webhook_callback_url: "https://api.example.com/whatsapp/webhooks/your-tenant",
      });
      setLoading(false);
      return;
    }
    if (entitlementsLoading) {
      return;
    }
    if (!whatsappEnabled) {
      setSettings(null);
      setLoading(false);
      setFeedback(null);
      return;
    }
    setLoading(true);
    setFeedback(null);
    try {
      const res = await axiosInstance.get("/whatsapp/settings");
      apply(res.data);
    } catch (err) {
      showError("load", getApiErrorMessage(err, "Could not load WhatsApp settings."));
    } finally {
      setLoading(false);
    }
  }, [apply, axiosInstance, entitlementsLoading, isPlayground, whatsappEnabled]);

  useEffect(() => {
    load();
  }, [load]);

  // A stored secret stays masked and read-only until the admin explicitly asks
  // to replace it. Clearing on focus (the old behaviour) looked like the saved
  // value had been thrown away just because the field was clicked.
  const replaceAccessToken = () => {
    setAccessTokenEdited(true);
    setAccessToken("");
    setShowAccessToken(false);
  };

  const keepAccessToken = () => {
    setAccessTokenEdited(false);
    setAccessToken(settings?.has_access_token ? MASK : "");
    setShowAccessToken(false);
  };

  const replaceAppSecret = () => {
    setAppSecretEdited(true);
    setAppSecret("");
    setShowAppSecret(false);
  };

  const keepAppSecret = () => {
    setAppSecretEdited(false);
    setAppSecret(settings?.has_app_secret ? MASK : "");
    setShowAppSecret(false);
  };

  const accessTokenStored = Boolean(settings?.has_access_token) && !accessTokenEdited;
  const appSecretStored = Boolean(settings?.has_app_secret) && !appSecretEdited;

  /**
   * Only a secret the admin actually entered is sent.
   *
   * These are password-type inputs, so a browser password manager will happily
   * fill them. Sending whatever is present stored that autofilled value as the
   * Access Token, and Meta then rejected it with "Cannot parse access token".
   */
  const secretPayload = (entered, value) => {
    if (!entered) return undefined;
    const v = String(value || "").trim();
    if (!v || v.includes("•")) return undefined;
    return v;
  };

  const buildSavePayload = () => {
    const nextToken = secretPayload(accessTokenEdited, accessToken);
    const nextSecret = secretPayload(appSecretEdited, appSecret);
    if ((nextToken || nextSecret) && settings && !settings.encryption_available) {
      throw new Error(
        "Access Token and App Secret cannot be saved until SECRET_ENCRYPTION_KEYS is configured on the API server."
      );
    }
    if (
      !phoneNumberId.trim() &&
      !businessAccountId.trim() &&
      !nextToken &&
      !nextSecret &&
      !verifyToken.trim()
    ) {
      throw new Error("Enter at least one field to save.");
    }
    const payload = {
      business_account_id: businessAccountId.trim() || null,
      phone_number_id: phoneNumberId.trim() || null,
      verify_token: verifyToken.trim() || null,
    };
    if (nextToken) payload.access_token = nextToken;
    if (nextSecret) payload.app_secret = nextSecret;
    return payload;
  };

  const blockedReason = () => {
    if (!canManage) return "You need the Manage Integrations permission to change WhatsApp settings.";
    if (fieldsLocked) return "WhatsApp settings cannot be changed in playground mode.";
    return null;
  };

  /**
   * Persist the credential form. Callers own the `busy` flag so only the
   * button that was pressed shows a spinner.
   *
   * `scope` routes the banner to the section that asked for the save, so a
   * failure triggered by Subscribe is reported under Subscribe rather than
   * back up in the credentials block.
   */
  const persistSettings = async ({ quiet = false, scope = "save" } = {}) => {
    try {
      const payload = buildSavePayload();
      const res = await axiosInstance.put("/whatsapp/settings", payload);
      apply(res.data);
      if (!quiet) {
        showSuccess(scope, "WhatsApp settings saved successfully.");
      }
      return res.data;
    } catch (err) {
      const message =
        err instanceof Error && !err.response
          ? err.message
          : getApiErrorMessage(err, "Could not save WhatsApp settings.");
      showError(scope, message);
      return null;
    }
  };

  const save = async () => {
    const blocked = blockedReason();
    if (blocked) {
      showError("save", blocked);
      return;
    }
    setBusy("save");
    setFeedback(null);
    try {
      await persistSettings({ scope: "save" });
    } finally {
      setBusy(null);
    }
  };

  const generateToken = async () => {
    const blocked = blockedReason();
    if (blocked) {
      showError("webhook", blocked);
      return;
    }
    setBusy("token");
    setFeedback(null);
    try {
      const res = await axiosInstance.post("/whatsapp/settings/generate-token");
      apply(res.data);
      showSuccess("webhook", "New verify token generated. Paste it into Meta webhook settings.");
    } catch (err) {
      showError("webhook", getApiErrorMessage(err, "Could not generate verify token."));
    } finally {
      setBusy(null);
    }
  };

  const credentialsDirty = () => {
    // Auto-persist before Subscribe / Send Test must not run when nothing
    // changed: a full PUT with empty plain fields would null out values that
    // are already in the DB, and after a refresh that looked exactly like
    // "already saved but test says not saved".
    const same = (a, b) => String(a || "").trim() === String(b || "").trim();
    return (
      accessTokenEdited ||
      appSecretEdited ||
      !same(phoneNumberId, settings?.phone_number_id) ||
      !same(businessAccountId, settings?.business_account_id) ||
      !same(verifyToken, settings?.verify_token)
    );
  };

  const ensurePersisted = async (scope) => {
    if (!credentialsDirty()) {
      return settings;
    }
    return persistSettings({ quiet: true, scope });
  };

  const toggleSubscribe = async (subscribe) => {
    const blocked = blockedReason();
    if (blocked) {
      showError("subscribe", blocked);
      return;
    }
    setBusy(subscribe ? "subscribe" : "unsubscribe");
    setFeedback(null);
    try {
      // Subscribe reads credentials from the database, so persist the form
      // first only when the admin actually changed something.
      if (subscribe) {
        const saved = await ensurePersisted("subscribe");
        if (!saved) return;

        const missing = [];
        if (!saved.business_account_id) missing.push("Business Account ID");
        if (!saved.phone_number_id) missing.push("Phone Number ID");
        if (!saved.has_access_token) missing.push("Access Token");
        if (!saved.has_app_secret) missing.push("App Secret");
        if (missing.length) {
          showError(
            "subscribe",
            `Cannot subscribe yet — ${missing.join(", ")} not saved. Use Replace on each ` +
              "secret field, paste the real value, then Save WhatsApp Settings."
          );
          return;
        }
      }
      const path = subscribe ? "/whatsapp/settings/subscribe" : "/whatsapp/settings/unsubscribe";
      const res = await axiosInstance.post(path);
      apply(res.data);
      showSuccess(
        "subscribe",
        subscribe
          ? "Subscribed with Meta. WhatsApp is now Connected."
          : "WhatsApp integration unsubscribed."
      );
    } catch (err) {
      showError(
        "subscribe",
        getApiErrorMessage(err, subscribe ? "Subscribe failed." : "Could not unsubscribe.")
      );
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    const blocked = blockedReason();
    if (blocked) {
      showError("test", blocked);
      return;
    }
    const to = testRecipient.trim();
    if (!to) {
      setTestResult({ ok: false, message: "Enter a recipient phone number in international format." });
      return;
    }
    setBusy("test");
    setTestResult(null);
    setFeedback(null);
    try {
      // After refresh the form already mirrors the DB. Re-saving every time
      // was pointless and could wipe Phone Number ID when state had not
      // finished hydrating — use stored settings unless something changed.
      const saved = await ensurePersisted("test");
      if (!saved) return;

      if (!saved.has_access_token || !saved.phone_number_id) {
        setTestResult({
          ok: false,
          message:
            "Phone Number ID and Access Token must be saved before sending a test message. " +
            "Use Replace on the Access Token field, paste the real token, then Save WhatsApp Settings.",
        });
        return;
      }
      const res = await axiosInstance.post("/whatsapp/settings/test-message", null, {
        params: { to },
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, message: getApiErrorMessage(err, "Test message failed.") });
    } finally {
      setBusy(null);
    }
  };

  if (loading || entitlementsLoading) {
    return (
      <MDBox display="flex" justifyContent="center" py={6}>
        <CircularProgress color="info" />
      </MDBox>
    );
  }

  if (!whatsappEnabled && !isPlayground) {
    return (
      <Alert severity="info" icon={<Lock />}>
        WhatsApp is a seat-licensed add-on. Enable it on this tenant&apos;s subscription in the
        Admin portal (quantity &gt; 0), then assign seats under Settings → User Management.
      </Alert>
    );
  }

  return (
    <MDBox>
      {!canManage && !isPlayground && whatsappEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You need <strong>Manage Integrations</strong> on your role to save WhatsApp settings.
          Ask a system administrator to grant that capability (or assign you the Administrator role).
        </Alert>
      )}

      {feedback?.scope === "load" && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <MDBox
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid #E6E8F0",
              bgcolor: "#fff",
            }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                WhatsApp Business
              </MDTypography>
              <Chip
                label={settings?.connected ? "Connected" : "Not Connected"}
                color={settings?.connected ? "success" : "default"}
                size="small"
              />
            </MDBox>

            <MDTypography variant="body2" color="text" mb={2}>
              Connect Meta WhatsApp Cloud API so Pearl can verify webhooks and send messages.
            </MDTypography>

            {!settings?.encryption_available && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Secret encryption is not configured on this server. Access tokens cannot be saved until{" "}
                <code>SECRET_ENCRYPTION_KEYS</code> is set.
              </Alert>
            )}

            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              1 · Meta credentials
            </MDTypography>
            <TextField
              fullWidth
              label="Business Account ID"
              value={businessAccountId}
              onChange={(e) => setBusinessAccountId(e.target.value)}
              margin="normal"
              size="small"
              disabled={fieldsLocked}
              helperText="From Meta → WhatsApp → API Setup (WABA ID)"
            />
            <TextField
              fullWidth
              label="Phone Number ID"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              margin="normal"
              size="small"
              disabled={fieldsLocked}
              helperText="The Meta ID of the business WhatsApp number used to send"
            />
            <SecretField
              label="Access Token"
              value={accessToken}
              onChange={(v) => {
                setAccessTokenEdited(true);
                setAccessToken(v);
              }}
              stored={accessTokenStored}
              storedHint={settings?.access_token_masked}
              onReplace={replaceAccessToken}
              onKeep={settings?.has_access_token ? keepAccessToken : null}
              visible={showAccessToken}
              onToggleVisible={() => setShowAccessToken((v) => !v)}
              locked={fieldsLocked}
              helper="Permanent token from a System User with whatsapp_business_messaging (starts with EAA)"
            />
            <SecretField
              label="App Secret"
              value={appSecret}
              onChange={(v) => {
                setAppSecretEdited(true);
                setAppSecret(v);
              }}
              stored={appSecretStored}
              storedHint={settings?.app_secret_masked}
              onReplace={replaceAppSecret}
              onKeep={settings?.has_app_secret ? keepAppSecret : null}
              visible={showAppSecret}
              onToggleVisible={() => setShowAppSecret((v) => !v)}
              locked={fieldsLocked}
              helper="From Meta App → Settings → Basic (32 hex characters). Verifies X-Hub-Signature-256."
            />

            <MDBox mt={2} display="flex" justifyContent="flex-end" alignItems="center" gap={2} flexWrap="wrap">
              <MDTypography variant="caption" color="text" sx={{ mr: "auto", maxWidth: 420 }}>
                Save Phone Number ID and Access Token here before Subscribe or Send Test Message.
              </MDTypography>
              <MDButton
                variant="contained"
                color="info"
                onClick={save}
                disabled={fieldsLocked || isBusy}
              >
                {busy === "save" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Save WhatsApp Settings"
                )}
              </MDButton>
            </MDBox>
            {feedback?.scope === "save" && (
              <Alert severity={feedback.severity} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              2 · Webhook
            </MDTypography>
            <TextField
              fullWidth
              label="Verify Token"
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value)}
              margin="normal"
              size="small"
              disabled={fieldsLocked}
              helperText="Must match the verify token in Meta webhook configuration"
            />
            <CopyField
              label="Webhook Callback URL"
              value={settings?.webhook_callback_url}
              helper="Paste this into Meta → WhatsApp → Configuration → Webhook"
            />
            <MDBox display="flex" gap={1} flexWrap="wrap" mt={1}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={generateToken}
                disabled={fieldsLocked || isBusy}
              >
                {busy === "token" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Generate New Token"
                )}
              </MDButton>
            </MDBox>
            {feedback?.scope === "webhook" && (
              <Alert severity={feedback.severity} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              3 · Enable integration
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2}>
              Subscribe calls Meta&apos;s subscribed_apps API for your WABA, then marks Pearl Connected.
              Configure the webhook URL and verify token in Meta first. Credentials are saved
              automatically when you click Subscribe.
            </MDTypography>
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                variant="gradient"
                color="success"
                size="small"
                onClick={() => toggleSubscribe(true)}
                disabled={fieldsLocked || isBusy || settings?.webhook_subscribed}
              >
                {busy === "subscribe" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Subscribe"
                )}
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => toggleSubscribe(false)}
                disabled={fieldsLocked || isBusy || !settings?.webhook_subscribed}
              >
                {busy === "unsubscribe" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Unsubscribe"
                )}
              </MDButton>
            </MDBox>
            {feedback?.scope === "subscribe" && (
              <Alert severity={feedback.severity} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              4 · Test message
            </MDTypography>
            <TextField
              fullWidth
              label="Test recipient phone"
              placeholder="e.g. 61412345678"
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              margin="normal"
              size="small"
              disabled={fieldsLocked}
              helperText="International format without + — must be a number that can receive WhatsApp"
            />
            {testResult && (
              <Alert severity={testResult.ok ? "success" : "error"} sx={{ mt: 1 }} onClose={() => setTestResult(null)}>
                {testResult.message}
              </Alert>
            )}
            {feedback?.scope === "test" && (
              <Alert severity={feedback.severity} sx={{ mt: 1 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
              <MDButton
                variant="outlined"
                color="info"
                onClick={sendTest}
                disabled={fieldsLocked || isBusy}
              >
                {busy === "test" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Send Test Message"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </Grid>

        <Grid item xs={12} lg={5}>
          <InstructionCard
            title="Setup guide"
            action={
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  href={META_DEVELOPERS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNew />}
                >
                  Meta Developers
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  href={META_BUSINESS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNew />}
                >
                  Business Manager
                </MDButton>
              </MDBox>
            }
          >
            <NumberedSteps
              steps={[
                "Create or open your app in Meta for Developers and add the WhatsApp product.",
                "In WhatsApp → API Setup, copy the Business Account ID and Phone number ID into Pearl.",
                "Create a permanent Access Token (System User with whatsapp_business_messaging) and paste it here.",
                "Copy the App Secret from Meta App → Settings → Basic into Pearl (required for webhook signature checks).",
                "Click Generate New Token in Pearl, then in Meta → WhatsApp → Configuration set the Callback URL and Verify token from this page.",
                "In Meta, subscribe to messages (and message status events).",
                "Click Subscribe in Pearl (calls Meta subscribed_apps), then send a test message to confirm delivery.",
              ]}
            />
          </InstructionCard>

          <InstructionCard title="Ops checklist">
            <NumberedSteps
              steps={[
                "Backend must be publicly reachable over HTTPS — Meta cannot call localhost.",
                "Set REACT_APP_API_URL to your public API base so the callback URL shown here is correct.",
                "Verify token in Pearl must exactly match Meta webhook settings.",
                "Connected requires Business Account ID, Phone Number ID, Access Token, App Secret, and Subscribe.",
              ]}
            />
          </InstructionCard>

          <InstructionCard title="Troubleshooting">
            <NumberedSteps
              steps={[
                "Webhook verify fails in Meta → check verify token match and callback URL tenant slug.",
                "Inbound posts return 403 → check App Secret matches Meta and X-Hub-Signature-256 is present.",
                "Test message fails → confirm Phone Number ID, token validity, and recipient is WhatsApp-enabled.",
                "Connected chip not showing → save all credentials (including App Secret) and click Subscribe.",
              ]}
            />
          </InstructionCard>
        </Grid>
      </Grid>
    </MDBox>
  );
}
